/**
 * t541u: driver tracking for transport managers. Pure rules shared by the API (ingest, stop detection,
 * retention), the web live map and the iOS app (offline queue). Location is only ever collected while
 * a journey the driver started is in progress (docs/fleet.md "Tracking").
 */
import { haversineM } from './logic';
import type { JourneyStop, LatLng } from './types';

/** One position as sent by a device. `recorded_at` is when the fix was taken (queued fixes arrive later). */
export interface PositionInput { lat: number; lng: number; accuracy_m?: number | null; recorded_at?: string | null; speed_mps?: number | null }
export interface AcceptedPosition { lat: number; lng: number; accuracy_m: number | null; recorded_at: string; speed_mps: number | null }
export type PositionRejectReason = 'bad_coords' | 'inaccurate' | 'bad_time' | 'future' | 'before_start' | 'too_old' | 'duplicate' | 'batch_too_large';

export const TRACKING = {
  /** Most positions one request may carry (a few hours of queued background fixes). */
  maxBatch: 200,
  /** A fix stamped more than this in the future is refused (device clock skew allowance). */
  futureSkewMs: 2 * 60_000,
  /** Fixes taken before the journey started (minus this allowance) are refused. */
  startSkewMs: 5 * 60_000,
  /** Fixes older than this are refused even if queued. */
  maxAgeMs: 24 * 3_600_000,
  /** Fixes worse than this are noise. */
  maxAccuracyM: 1000,
  /** Fixes closer together than this are treated as duplicates. */
  minGapMs: 15_000,
  /** The marker greys out and reads as stale after this long without an update. */
  staleAfterMin: 10,
  /** "Stopped" radius and the distance from a planned stop or the depot that doesn't count. */
  stoppedRadiusM: 100,
  stopAvoidRadiusM: 250,
  /** No "Stopped" label when the device has been silent this long (we can't tell). */
  stoppedMaxSilenceMin: 120,
  defaultStoppedMinutes: 15,
  defaultRetentionDays: 90,
} as const;

const validCoord = (lat: unknown, lng: unknown) =>
  typeof lat === 'number' && typeof lng === 'number' && Number.isFinite(lat) && Number.isFinite(lng) &&
  lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180 && !(lat === 0 && lng === 0);

/**
 * Server ingest rules for a batch of positions: valid coordinates, sensible accuracy, timestamps no later
 * than now (+skew), not before the journey started and not older than a day, and no near-duplicates of
 * each other or of fixes already stored. Returned in time order.
 */
export function validatePositionBatch(points: readonly PositionInput[], opts: { now: number; journeyStart: string | null; existing?: readonly string[] }): { accepted: AcceptedPosition[]; rejected: { index: number; reason: PositionRejectReason }[] } {
  const rejected: { index: number; reason: PositionRejectReason }[] = [];
  const start = opts.journeyStart ? Date.parse(opts.journeyStart) : null;
  const cands: (AcceptedPosition & { t: number; index: number })[] = [];
  points.forEach((p, index) => {
    if (index >= TRACKING.maxBatch) return rejected.push({ index, reason: 'batch_too_large' });
    if (!p || !validCoord(p.lat, p.lng)) return rejected.push({ index, reason: 'bad_coords' });
    const acc = p.accuracy_m == null ? null : Number(p.accuracy_m);
    if (acc != null && (!Number.isFinite(acc) || acc < 0 || acc > TRACKING.maxAccuracyM)) return rejected.push({ index, reason: 'inaccurate' });
    const t = p.recorded_at ? Date.parse(p.recorded_at) : opts.now;
    if (!Number.isFinite(t)) return rejected.push({ index, reason: 'bad_time' });
    if (t > opts.now + TRACKING.futureSkewMs) return rejected.push({ index, reason: 'future' });
    if (t < opts.now - TRACKING.maxAgeMs) return rejected.push({ index, reason: 'too_old' });
    if (start != null && t < start - TRACKING.startSkewMs) return rejected.push({ index, reason: 'before_start' });
    const speed = p.speed_mps == null || !Number.isFinite(Number(p.speed_mps)) || Number(p.speed_mps) < 0 ? null : Math.min(Number(p.speed_mps), 100);
    cands.push({ lat: p.lat, lng: p.lng, accuracy_m: acc == null ? null : Math.round(acc), recorded_at: new Date(Math.min(t, opts.now)).toISOString(), speed_mps: speed, t: Math.min(t, opts.now), index });
  });
  const taken = (opts.existing ?? []).map(Date.parse).filter(Number.isFinite).sort((a, b) => a - b);
  const near = (t: number) => taken.some(x => Math.abs(x - t) < TRACKING.minGapMs);
  const accepted: AcceptedPosition[] = [];
  for (const c of cands.sort((a, b) => a.t - b.t)) {
    if (near(c.t)) { rejected.push({ index: c.index, reason: 'duplicate' }); continue; }
    taken.push(c.t);
    const { t: _t, index: _i, ...rest } = c;
    accepted.push(rest);
  }
  return { accepted, rejected: rejected.sort((a, b) => a.index - b.index) };
}

export interface TrailPoint { lat: number; lng: number; at: string }

/**
 * "Stopped for a while": the latest fixes sit within `radiusM` of the latest one for at least `minMinutes`
 * (iOS sends nothing while stationary, so the clock runs from the first fix in the cluster to now), away
 * from planned stops and the depot. Null while moving, near a stop, or when the device has gone quiet.
 */
export function detectStopped(points: readonly TrailPoint[], opts: { now: number; minMinutes: number; avoid?: readonly (LatLng | null | undefined)[]; radiusM?: number; avoidRadiusM?: number; maxSilenceMin?: number }): { since: string; minutes: number; lat: number; lng: number } | null {
  if (!points.length || !(opts.minMinutes > 0)) return null;
  const sorted = [...points].sort((a, b) => Date.parse(a.at) - Date.parse(b.at));
  const last = sorted[sorted.length - 1];
  const radius = opts.radiusM ?? TRACKING.stoppedRadiusM;
  if (opts.now - Date.parse(last.at) > (opts.maxSilenceMin ?? TRACKING.stoppedMaxSilenceMin) * 60_000) return null;
  let i = sorted.length - 1;
  while (i > 0 && haversineM(sorted[i - 1], last) <= radius) i--;
  const since = Date.parse(sorted[i].at);
  const minutes = Math.floor((opts.now - since) / 60_000);
  if (minutes < opts.minMinutes) return null;
  const avoidR = opts.avoidRadiusM ?? TRACKING.stopAvoidRadiusM;
  if ((opts.avoid ?? []).some(a => a && validCoord(a.lat, a.lng) && haversineM(a, last) <= avoidR)) return null;
  return { since: new Date(since).toISOString(), minutes, lat: last.lat, lng: last.lng };
}

/** "just now", "2 min ago", "3 h ago", "2 days ago". */
export function agoLabel(iso: string | null | undefined, now: number): string {
  if (!iso) return 'No updates yet';
  const min = Math.max(0, Math.floor((now - Date.parse(iso)) / 60_000));
  if (min < 1) return 'just now';
  if (min < 60) return `${min} min ago`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h} h ago`;
  const d = Math.floor(h / 24);
  return d === 1 ? '1 day ago' : `${d} days ago`;
}
export const isStale = (iso: string | null | undefined, now: number, afterMin: number = TRACKING.staleAfterMin) => !iso || now - Date.parse(iso) > afterMin * 60_000;

/** "Stopped 18 min" / "Stopped 1 h 5 min" (neutral wording: never implies the driver did something wrong). */
export function stoppedLabel(minutes: number): string {
  if (minutes < 60) return `Stopped ${minutes} min`;
  const h = Math.floor(minutes / 60), m = minutes % 60;
  return m ? `Stopped ${h} h ${m} min` : `Stopped ${h} h`;
}

export type StopTiming = { kind: 'early' | 'on_time' | 'late'; minutes: number } | { kind: 'unknown'; minutes: 0 };
/** Early/late against the stop's window, by arrival when arrived, else by ETA. */
export function stopTiming(s: Pick<JourneyStop, 'window_start' | 'window_end' | 'eta' | 'arrived_at'>, graceMin = 10): StopTiming {
  const at = s.arrived_at ?? s.eta;
  if (!at || (!s.window_start && !s.window_end)) return { kind: 'unknown', minutes: 0 };
  const t = Date.parse(at);
  if (s.window_end && t > Date.parse(s.window_end) + graceMin * 60_000) return { kind: 'late', minutes: Math.round((t - Date.parse(s.window_end)) / 60_000) };
  if (s.window_start && t < Date.parse(s.window_start) - graceMin * 60_000) return { kind: 'early', minutes: Math.round((Date.parse(s.window_start) - t) / 60_000) };
  return { kind: 'on_time', minutes: 0 };
}
export function timingLabel(t: StopTiming): string | null {
  if (t.kind === 'unknown') return null;
  if (t.kind === 'on_time') return 'On time';
  return `${t.minutes} min ${t.kind}`;
}

/** Positions older than this instant are deleted by the nightly retention job. */
export const retentionCutoff = (now: number, days: number) => new Date(now - Math.max(1, Math.round(days)) * 86_400_000).toISOString();

/** Evenly thin a trail to at most `max` points, always keeping the first and the latest. */
export function downsample<T>(points: readonly T[], max: number): T[] {
  if (points.length <= max || max < 2) return [...points];
  const out: T[] = []; const step = (points.length - 1) / (max - 1);
  for (let i = 0; i < max; i++) out.push(points[Math.round(i * step)]);
  return out;
}

export interface LiveStop extends Pick<JourneyStop, 'id' | 'seq' | 'label' | 'lat' | 'lng' | 'status' | 'eta' | 'window_start' | 'window_end' | 'arrived_at' | 'completed_at' | 'late'> { timing: StopTiming }
/** GET /fleet/live: one in-progress journey's vehicle. */
export interface LiveVehicle {
  journey_id: string; ref: string; reg: string | null; driver_name: string | null; origin_name: string | null; origin: LatLng | null;
  planned_start: string; actual_start: string | null; tracking_consent: boolean;
  position: (LatLng & { at: string; accuracy_m: number | null }) | null;
  trail: TrailPoint[]; stops: LiveStop[]; next_stop: LiveStop | null; done_count: number; stop_count: number;
  stopped: { since: string; minutes: number } | null;
}
export interface LiveMap { vehicles: LiveVehicle[]; stale_after_min: number; generated_at: string }
/** GET /fleet/journeys/:id/trail (managers) and run/:id/trail (the driver's own). */
export interface JourneyTrail { journey_id: string; status: string; points: TrailPoint[]; stops: LiveStop[]; origin: LatLng | null; retention_days: number; actual_start: string | null; actual_end: string | null }
export interface PositionsResult { ok: boolean; accepted: number; rejected: number; reason?: 'not_in_progress' | 'no_consent' | 'not_driver' }
